import type {
  PersistedClient,
  Persister,
} from '@tanstack/query-persist-client-core'
import { createStore, get, keys, promisifyRequest } from 'idb-keyval'

import type { CacheGeneration } from './cache-scope'
import { cacheKeyOwner, cacheUserPrefixes } from './cache-scope'

const ownedStore = createStore('keyval-store', 'keyval')
const deletedKey = (owner: string) => ['deleted-cache-owner-v1', owner]
const generationKey = (owner: string) => ['cache-owner-generation-v1', owner]
const forgottenKey = (owner: string, operation: string) => [
  'forgotten-cache-operation-v1',
  owner,
  operation,
]

export async function deleteIndexedDBUserCaches(userId: string): Promise<void> {
  const prefixes = cacheUserPrefixes(userId)
  await ownedStore('readwrite', store => {
    store.put(true, deletedKey(userId))
    const cursor = store.openCursor()
    cursor.onsuccess = () => {
      const row = cursor.result
      if (!row) return
      if (
        typeof row.key === 'string' &&
        prefixes.some(prefix => row.key.toString().startsWith(prefix))
      )
        row.delete()
      row.continue()
    }
    return promisifyRequest(store.transaction)
  })
}

export async function forgetIndexedDBUserCaches(
  userId: string,
  operationId: string,
): Promise<void> {
  if (!userId || !operationId || operationId.length > 128)
    throw new Error('Cache removal requires an owner and operation')
  const prefixes = cacheUserPrefixes(userId)
  const db = await ownedStore('readonly', store => store.transaction.db)
  const tx = db.transaction('keyval', 'readwrite', { durability: 'strict' })
  const store = tx.objectStore('keyval')
  await (() => {
    const prior = store.get(forgottenKey(userId, operationId))
    prior.onsuccess = () => {
      if (prior.result !== undefined) {
        if (prior.result !== true) store.transaction.abort()
        return
      }
      store.put(true, forgottenKey(userId, operationId))
      store.put(operationId, generationKey(userId))
      const cursor = store.openCursor()
      cursor.onsuccess = () => {
        const row = cursor.result
        if (!row) return
        if (
          typeof row.key === 'string' &&
          prefixes.some(prefix => row.key.toString().startsWith(prefix))
        )
          row.delete()
        row.continue()
      }
    }
    return promisifyRequest(store.transaction)
  })()
}

export async function removeIndexedDBCaches(
  prefix: string,
  expected?: CacheGeneration,
): Promise<void> {
  await ownedStore('readwrite', store => {
    const remove = () => {
      const cursor = store.openCursor()
      cursor.onsuccess = () => {
        const row = cursor.result
        if (!row) return
        if (typeof row.key === 'string' && row.key.startsWith(prefix))
          row.delete()
        row.continue()
      }
    }
    if (expected) {
      const generation = store.get(generationKey(expected.ownerId))
      generation.onsuccess = () => {
        if ((generation.result ?? null) === expected.generation) remove()
      }
    } else remove()
    return promisifyRequest(store.transaction)
  })
}

export async function createIndexedDBPersister(
  cacheKey: string,
  scopePrefix = cacheKey,
): Promise<Persister> {
  const owner = cacheKeyOwner(cacheKey)
  const generation: unknown = owner
    ? ((await get(generationKey(owner), ownedStore)) ?? null)
    : null
  if (generation !== null && typeof generation !== 'string')
    throw new Error('Cache generation is invalid')
  const current = async () =>
    !owner ||
    ((await get(deletedKey(owner), ownedStore)) === undefined &&
      ((await get(generationKey(owner), ownedStore)) ?? null) === generation)
  return {
    persistClient: async (client: PersistedClient) => {
      try {
        await ownedStore('readwrite', store => {
          if (owner) {
            const marker = store.get(deletedKey(owner))
            marker.onsuccess = () => {
              if (marker.result !== undefined) return
              const current = store.get(generationKey(owner))
              current.onsuccess = () => {
                if ((current.result ?? null) === generation)
                  store.put(client, cacheKey)
              }
            }
          } else store.put(client, cacheKey)
          return promisifyRequest(store.transaction)
        })
      } catch {
        console.warn('[QueryCache:IDB] Failed to persist client')
      }
    },
    restoreClient: async () => {
      try {
        if (!(await current())) return undefined
        const saved = await get<PersistedClient>(cacheKey)
        if (saved) return (await current()) ? saved : undefined
        // Migrate only this exact account/org/access scope. Keep old snapshots
        // until a successful write, including any queued offline commands.
        const legacyKeys = (await keys()).filter(
          key =>
            typeof key === 'string' &&
            key.startsWith(scopePrefix) &&
            !key.slice(scopePrefix.length).startsWith('schema-') &&
            key !== cacheKey,
        )
        const snapshots = await Promise.all(
          legacyKeys.map(key => get<PersistedClient>(key)),
        )
        if (!(await current())) return undefined
        return snapshots
          .filter((value): value is PersistedClient => !!value)
          .sort((a, b) => b.timestamp - a.timestamp)[0]
      } catch {
        console.warn('[QueryCache:IDB] Failed to restore client')
        return undefined
      }
    },
    removeClient: async () => {
      try {
        await removeIndexedDBCaches(
          scopePrefix,
          owner
            ? { ownerId: owner, generation: generation as string | null }
            : undefined,
        )
      } catch {
        console.warn('[QueryCache:IDB] Failed to remove client')
      }
    },
  }
}
